import React, { useEffect, useRef, useState } from 'react';
import {
    X, Lock, Link2, Globe, Copy, Check, Share2, FileText, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API = '/api';

const VISIBILITY_OPTIONS = [
    {
        value: 'private',
        label: 'Private',
        icon: Lock,
        caption: 'Only you can see this bookmark.',
    },
    {
        value: 'unlisted',
        label: 'Unlisted',
        icon: Link2,
        caption: 'Anyone with the link can view. Not indexed.',
    },
    {
        value: 'public',
        label: 'Public',
        icon: Globe,
        caption: 'Anyone can view. Shared publicly.',
    },
];

/**
 * Universal ShareModal for bookmarks and categories.
 *
 * Handles:
 *  - Privacy toggle (private / unlisted / public) - POSTs to the right endpoint
 *  - Copy public link (with ?ref= referral tag auto-appended)
 *  - Copy as Markdown (title + AI summary + link, ready for Notion/Slack)
 *  - Native Web Share sheet (navigator.share) - auto on TWA
 *  - Dedicated WhatsApp / Messages / X / LinkedIn buttons
 *
 * Props:
 *  - kind: "bookmark" | "category"
 *  - item: full bookmark or category object
 *  - currentUser: { user_id } - used to append ?ref=
 *  - onVisibilityChange: (newVisibility) => void - called after successful change
 */
export default function ShareModal({ open, onClose, kind = 'bookmark', item, currentUser, onVisibilityChange }) {
    const [visibility, setVisibility] = useState(item?.share_visibility || 'unlisted');
    const [saving, setSaving] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [copiedMd, setCopiedMd] = useState(false);
    const copyTimer = useRef(null);

    // Sync local state whenever the item changes (e.g. user reopens modal on a different bookmark)
    useEffect(() => {
        if (open) {
            setVisibility(item?.share_visibility || 'unlisted');
            setCopiedLink(false);
            setCopiedMd(false);
        }
    }, [open, item?.bookmark_id, item?.category_id, item?.share_visibility]);

    if (!open || !item) return null;

    const itemId = kind === 'bookmark' ? item.bookmark_id : item.category_id;
    const title = item.title || item.name || (kind === 'bookmark' ? 'Bookmark' : 'Collection');
    const summary = item.summary ? (item.summary.split('\n').find(l => l.trim()) || '').slice(0, 200) : '';
    const sourceUrl = item.url || '';

    // Build shareable URL - route through the backend OG endpoint so social
    // media crawlers (WhatsApp, Telegram, Twitter, etc.) get bookmark-specific
    // Open Graph tags. Real browsers are redirected by the backend to the
    // React SPA `/share/:id` route automatically (see routes/share.py).
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://brainsync.pro';
    const refParam = currentUser?.user_id ? `?ref=${encodeURIComponent(currentUser.user_id)}` : '';
    const publicPath = kind === 'bookmark' ? `/api/share/${itemId}` : `/c/${itemId}`;
    const publicUrl = `${origin}${publicPath}${refParam}`;

    const canonicalShareText = `Check out this ${kind === 'bookmark' ? title : 'collection'} I saved on BrainSync`;
    const markdown = kind === 'bookmark'
        ? `**${title}**${summary ? `\n\n${summary}` : ''}${sourceUrl ? `\n\n[Open original](${sourceUrl})` : ''}\n\nSaved on BrainSync: ${publicUrl}`
        : `**${title}** - a BrainSync collection\n\n${publicUrl}`;

    const endpoint = kind === 'bookmark'
        ? `${API}/bookmarks/${itemId}/share`
        : `${API}/categories/${itemId}/share`;

    const changeVisibility = async (newVis) => {
        if (newVis === visibility) return;
        setSaving(true);
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visibility: newVis }),
            });
            if (!res.ok) throw new Error(await res.text());
            setVisibility(newVis);
            onVisibilityChange?.(newVis);
            toast.success(
                newVis === 'private' ? 'Set to private' :
                newVis === 'unlisted' ? 'Share link is now active - only people with the link can view.' :
                'Made public - anyone can now find this.'
            );
        } catch (err) {
            toast.error('Could not update share settings');
        } finally {
            setSaving(false);
        }
    };

    // Silent upgrade from `private` → `unlisted` when the user explicitly
    // initiates a share. Forcing them to also flip the visibility toggle is
    // an extra step that broke the WhatsApp / native-share flow - recipients
    // just got the generic site OG card instead of the bookmark preview.
    // `unlisted` means: link works, but the bookmark won't appear in any
    // public discovery surfaces. That's the right default for "I just sent
    // this to a friend".
    const ensureShareable = async () => {
        if (visibility !== 'private') return true;
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visibility: 'unlisted' }),
            });
            if (!res.ok) throw new Error(await res.text());
            setVisibility('unlisted');
            onVisibilityChange?.('unlisted');
            return true;
        } catch (_) {
            toast.error('Could not enable sharing - try again');
            return false;
        }
    };

    const copy = async (text, which) => {
        if (which === 'link') {
            const ok = await ensureShareable();
            if (!ok) return;
        }
        try {
            await navigator.clipboard.writeText(text);
            if (which === 'link') {
                setCopiedLink(true);
                clearTimeout(copyTimer.current);
                copyTimer.current = setTimeout(() => setCopiedLink(false), 2000);
            } else {
                setCopiedMd(true);
                clearTimeout(copyTimer.current);
                copyTimer.current = setTimeout(() => setCopiedMd(false), 2000);
            }
            toast.success('Copied to clipboard');
        } catch (_) {
            toast.error('Could not copy - your browser blocked it');
        }
    };

    const openShare = async () => {
        const ok = await ensureShareable();
        if (!ok) return;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${title} | BrainSync`,
                    text: canonicalShareText,
                    url: publicUrl,
                });
            } catch (_) { /* user cancelled */ }
        } else {
            copy(publicUrl, 'link');
        }
    };

    const shareTo = async (channel) => {
        const ok = await ensureShareable();
        if (!ok) return;
        const encodedUrl = encodeURIComponent(publicUrl);
        const encodedText = encodeURIComponent(canonicalShareText);
        const fullMessage = `${canonicalShareText}\n${publicUrl}`;
        switch (channel) {
            case 'whatsapp':
                // whatsapp:// deep link triggers Android's app chooser when both
                // personal & business are installed.
                window.location.href = `whatsapp://send?text=${encodeURIComponent(fullMessage)}`;
                return;
            case 'telegram':
                window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, '_blank', 'noopener,noreferrer');
                return;
            case 'twitter':
                window.open(`https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank', 'noopener,noreferrer');
                return;
            case 'facebook':
                window.location.href = `fb-messenger://share/?link=${encodedUrl}`;
                return;
            case 'instagram':
                try { await navigator.clipboard.writeText(fullMessage); } catch (_) {}
                toast.success('Link copied - paste it in your Instagram DM');
                setTimeout(() => window.open('https://www.instagram.com/direct/inbox/', '_blank'), 400);
                return;
            case 'email':
                window.open(`mailto:?subject=${encodeURIComponent(`${title} | BrainSync`)}&body=${encodeURIComponent(fullMessage)}`, '_self');
                return;
            case 'sms':
                window.open(`sms:?body=${encodeURIComponent(fullMessage)}`, '_self');
                return;
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank', 'noopener,noreferrer');
                return;
            default:
                return;
        }
    };

    // Share buttons are always enabled - clicking auto-upgrades a `private`
    // bookmark to `unlisted` so the recipient's link works. Visibility toggle
    // remains as an explicit control for users who want stricter privacy.
    const isShareable = true;
    const hasNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
            data-testid="share-modal"
            onClick={onClose}
        >
            <div
                className="relative w-full sm:max-w-md bg-background rounded-t-3xl sm:rounded-3xl border border-border shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-secondary transition-colors z-10"
                    data-testid="share-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="px-6 pt-7 pb-3">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Share2 className="w-4 h-4 text-primary" />
                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                            Share
                        </span>
                    </div>
                    <h2 className="text-lg font-bold text-foreground line-clamp-2" style={{ lineHeight: '1.3' }}>
                        {title}
                    </h2>
                </div>

                {/* Privacy selector */}
                <div className="px-6 pb-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2.5">Who can view</p>
                    <div className="space-y-2" data-testid="share-visibility-options">
                        {VISIBILITY_OPTIONS.map(({ value, label, icon: Icon, caption }) => {
                            const selected = visibility === value;
                            return (
                                <button
                                    key={value}
                                    type="button"
                                    disabled={saving}
                                    onClick={() => changeVisibility(value)}
                                    className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                                        selected
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border bg-white hover:bg-secondary/40'
                                    } ${saving ? 'opacity-60 cursor-wait' : ''}`}
                                    data-testid={`share-visibility-${value}`}
                                >
                                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className={`text-sm font-semibold ${selected ? 'text-foreground' : 'text-foreground'}`}>
                                                {label}
                                            </p>
                                            {selected && <Check className="w-4 h-4 text-primary" />}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">{caption}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Actions (grouped so they dim when private) */}
                <div className={`px-6 pb-6 space-y-4 ${isShareable ? '' : 'opacity-50 pointer-events-none select-none'}`}>
                    {/* Shareable link */}
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Share Link</label>
                        <div className="flex gap-2">
                            <div className="flex-1 px-3 py-2 text-xs bg-muted rounded-lg truncate flex items-center font-mono" data-testid="share-link-preview">
                                {publicUrl.replace(/^https?:\/\//, '')}
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copy(publicUrl, 'link')}
                                data-testid="share-copy-link-btn"
                            >
                                {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>

                    {/* Share via socials */}
                    <div className="flex items-center gap-2 justify-center flex-wrap" data-testid="share-social-buttons">
                        <button
                            onClick={() => shareTo('whatsapp')}
                            className="w-10 h-10 rounded-full flex items-center justify-center bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                            title="Share via WhatsApp"
                            data-testid="share-whatsapp-btn"
                        >
                            <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        </button>
                        <button
                            onClick={() => shareTo('telegram')}
                            className="w-10 h-10 rounded-full flex items-center justify-center bg-[#0088cc]/10 hover:bg-[#0088cc]/20 transition-colors"
                            title="Share via Telegram"
                            data-testid="share-telegram-btn"
                        >
                            <svg className="w-5 h-5 text-[#0088cc]" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                        </button>
                        <button
                            onClick={() => shareTo('twitter')}
                            className="w-10 h-10 rounded-full flex items-center justify-center bg-black/5 hover:bg-black/10 transition-colors"
                            title="Share on X"
                            data-testid="share-x-btn"
                        >
                            <svg className="w-4 h-4 text-foreground" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </button>
                        <button
                            onClick={() => shareTo('facebook')}
                            className="w-10 h-10 rounded-full flex items-center justify-center bg-[#1877F2]/10 hover:bg-[#1877F2]/20 transition-colors"
                            title="Share on Facebook"
                            data-testid="share-facebook-btn"
                        >
                            <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        </button>
                        <button
                            onClick={() => shareTo('instagram')}
                            className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-[#F58529]/10 via-[#DD2A7B]/10 to-[#8134AF]/10 hover:from-[#F58529]/20 hover:via-[#DD2A7B]/20 hover:to-[#8134AF]/20 transition-colors"
                            title="Share via Instagram"
                            data-testid="share-instagram-btn"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#bs-ig-grad)"><defs><linearGradient id="bs-ig-grad" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#F58529"/><stop offset="50%" stopColor="#DD2A7B"/><stop offset="100%" stopColor="#8134AF"/></linearGradient></defs><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                        </button>
                        <button
                            onClick={() => shareTo('email')}
                            className="w-10 h-10 rounded-full flex items-center justify-center bg-muted hover:bg-muted/70 transition-colors"
                            title="Share via Email"
                            data-testid="share-email-btn"
                        >
                            <Mail className="w-5 h-5 text-muted-foreground" />
                        </button>
                        {hasNativeShare && (
                            <button
                                onClick={openShare}
                                className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10 hover:bg-primary/20 transition-colors"
                                title="More sharing options"
                                data-testid="share-native-btn"
                            >
                                <Share2 className="w-5 h-5 text-primary" />
                            </button>
                        )}
                    </div>

                    {/* Copy as Markdown */}
                    {kind === 'bookmark' && (
                        <Button
                            onClick={() => copy(markdown, 'md')}
                            variant="outline"
                            size="sm"
                            className="w-full rounded-xl text-xs"
                            data-testid="share-copy-markdown-btn"
                        >
                            {copiedMd ? <Check className="w-3.5 h-3.5 text-emerald-600 mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                            {copiedMd ? 'Markdown copied!' : 'Copy as Markdown'}
                        </Button>
                    )}

                    {/* Referral hint */}
                    {currentUser?.user_id && (
                        <p className="text-[11px] text-center text-muted-foreground/80 pt-1" data-testid="share-referral-hint">
                            <span className="inline-flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span> If a new user signs up via your link, you both get <span className="font-semibold text-foreground">+10 Premium AI saves</span></span>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
